import { Component } from "@theme/component";
import { DialogCloseEvent, DialogOpenEvent } from "@theme/dialog";
class HeaderComponent extends Component {
  static #DESKTOP_BREAKPOINT_PX = 768;
  // IDs of megamenu dialog-components currently open. Drives shared
  // backdrop visibility together with the mobile-nav open state.
  #openMegamenus = /* @__PURE__ */ new Set();
  // Scroll-state tracking (drives the `data-scrolled` glassy header bg).
  #scrollTicking = false;
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener("keydown", this.#onKeyDown);
    window.addEventListener("resize", this.#onResize);
    window.addEventListener("scroll", this.#onScroll, { passive: true });
    this.#updateScrolledAttr();
    this.#observeHeaderHeight();
    this.#portalMegamenusToBody();
    this.#portalBackdropToBody();
    document.addEventListener("click", this.#onClick);
    document.addEventListener(DialogOpenEvent.eventName, this.#onDialogOpen);
    document.addEventListener(DialogCloseEvent.eventName, this.#onDialogClose);
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener("keydown", this.#onKeyDown);
    window.removeEventListener("resize", this.#onResize);
    window.removeEventListener("scroll", this.#onScroll);
    document.removeEventListener("click", this.#onClick);
    document.removeEventListener(DialogOpenEvent.eventName, this.#onDialogOpen);
    document.removeEventListener(
      DialogCloseEvent.eventName,
      this.#onDialogClose
    );
    this.#setBodyScrollLock(false);
  }
  #portalMegamenusToBody() {
    const triggers = this.querySelectorAll(
      "[data-megamenu-trigger][aria-controls]"
    );
    triggers.forEach((trigger) => {
      const id = trigger.getAttribute("aria-controls");
      if (!id) return;
      const dialog = this.querySelector(
        `dialog-component#${CSS.escape(id)}`
      );
      if (!dialog || dialog.parentElement === document.body) return;
      document.body.appendChild(dialog);
    });
  }
  /**
   * Move the shared backdrop to <body> so it survives when this header
   * is marked `inert` by an open non-modal dialog (inert is inherited;
   * any descendant of an inert element is also inert, which would block
   * clicks on the backdrop).
   */
  #portalBackdropToBody() {
    const backdrop = this.#backdrop;
    if (!backdrop || backdrop.parentElement === document.body) return;
    document.body.appendChild(backdrop);
  }
  get #menuTrigger() {
    return this.querySelector("[data-menu-trigger]");
  }
  get #nav() {
    return this.querySelector("[data-header-nav]");
  }
  get #backdrop() {
    return document.querySelector("[data-header-nav-backdrop]");
  }
  get #closeButton() {
    return this.querySelector("[data-menu-close]");
  }
  get #searchTrigger() {
    return this.querySelector("[data-search-trigger]");
  }
  get #searchDialog() {
    return this.querySelector(
      "dialog-component#header-search-dialog"
    );
  }
  get #isMobileNavOpen() {
    return this.#nav?.getAttribute("data-open") === "true";
  }
  #onClick = (event) => {
    const target = event.target;
    if (!target) return;
    if (target.closest("[data-menu-trigger]")) {
      this.#toggleMobileNav();
      return;
    }
    if (target.closest("[data-menu-close]") || target.closest("[data-header-nav-backdrop]")) {
      this.#closeAllMegamenus();
      this.#closeMobileNav();
      return;
    }
    if (target.closest("[data-search-trigger]")) {
      this.#openSearch();
      return;
    }
    const megamenuTrigger = target.closest(
      "[data-megamenu-trigger]"
    );
    if (megamenuTrigger) {
      this.#toggleMegamenu(megamenuTrigger);
      return;
    }
    const megamenuBack = target.closest("[data-megamenu-back]");
    if (megamenuBack) {
      const dialogComponent = megamenuBack.closest("dialog-component");
      dialogComponent?.closeDialog?.();
      return;
    }
    const linkInNav = target.closest("[data-header-nav] a");
    const linkInMegamenu = target.closest("dialog.header-menu-megamenu a");
    if (linkInNav || linkInMegamenu) {
      const openMegamenu = target.closest("dialog-component");
      openMegamenu?.closeDialog?.();
      this.#closeMobileNav();
    }
  };
  #onKeyDown = (event) => {
    if (event.key === "Escape" && this.#isMobileNavOpen) {
      event.preventDefault();
      this.#closeMobileNav();
    }
  };
  #onResize = () => {
    if (this.#isMobileNavOpen && window.innerWidth >= HeaderComponent.#DESKTOP_BREAKPOINT_PX) {
      this.#closeMobileNav({ restoreFocus: false });
    }
  };
  #onScroll = () => {
    if (this.#scrollTicking) return;
    this.#scrollTicking = true;
    requestAnimationFrame(() => {
      this.#scrollTicking = false;
      this.#updateScrolledAttr();
    });
  };
  #updateScrolledAttr() {
    document.documentElement.removeAttribute("data-header-restored");
    const group = document.getElementById("header-group");
    if (!group) return;
    if (window.scrollY > 0) {
      group.setAttribute("data-scrolled", "");
    } else {
      group.removeAttribute("data-scrolled");
    }
    this.#updateAnnouncementOffset();
  }
  #baseBarHeight = 0;
  #updateAnnouncementOffset() {
    if (this.#baseBarHeight === 0) return;
    const realScrollY = Math.abs(parseInt(document.body.style.top) || 0) || window.scrollY;
    const visible = Math.max(0, this.#baseBarHeight - realScrollY);
    document.documentElement.style.setProperty(
      "--announcement-bar-height",
      `${visible}px`
    );
  }
  /** Keep --header-height in sync with header-only height, bar from section wrapper. */
  #observeHeaderHeight() {
    const group = document.getElementById("header-group");
    const bar = document.querySelector(
      ".shopify-section.announcement-bar"
    );
    if (!group) return;
    const update = () => {
      const barH = bar?.offsetHeight ?? 0;
      const barInGroup = bar ? group.contains(bar) : false;
      this.#baseBarHeight = barH;
      document.documentElement.style.setProperty(
        "--header-height",
        `${barInGroup ? group.offsetHeight - barH : group.offsetHeight}px`
      );
    };
    update();
    this.#updateAnnouncementOffset();
    const ro = new ResizeObserver(() => {
      update();
      this.#updateAnnouncementOffset();
    });
    ro.observe(group);
    if (bar) ro.observe(bar);
  }
  #toggleMobileNav() {
    if (this.#isMobileNavOpen) {
      this.#closeMobileNav();
    } else {
      this.#openMobileNav();
    }
  }
  #openMobileNav() {
    const nav = this.#nav;
    const trigger = this.#menuTrigger;
    if (!nav || !trigger) return;
    const group = document.getElementById("header-group");
    const bar = document.querySelector(
      ".shopify-section.announcement-bar"
    );
    const groupH = group?.offsetHeight ?? 0;
    const barH = bar?.offsetHeight ?? 0;
    const barInGroup = bar ? group?.contains(bar) : false;
    const headerOnlyH = barInGroup ? groupH - barH : groupH;
    const realScrollY = Math.abs(parseInt(document.body.style.top) || 0) || window.scrollY;
    const visibleBarH = Math.max(0, barH - realScrollY);
    nav.style.top = `${headerOnlyH + visibleBarH}px`;
    this.#updateAnnouncementOffset();
    nav.setAttribute("data-open", "true");
    trigger.setAttribute("aria-expanded", "true");
    trigger.setAttribute(
      "aria-label",
      trigger.dataset.closeLabel ?? "Close menu"
    );
    document.getElementById("header-group")?.setAttribute("data-mobile-nav-open", "");
    this.#showBackdrop();
    this.#setBodyScrollLock(true);
    requestAnimationFrame(() => {
      this.#closeButton?.focus({ preventScroll: true });
    });
  }
  #closeMobileNav(options = {}) {
    const { restoreFocus = true } = options;
    const nav = this.#nav;
    const trigger = this.#menuTrigger;
    if (!nav || !trigger) return;
    if (!this.#isMobileNavOpen) return;
    nav.setAttribute("data-open", "false");
    trigger.setAttribute("aria-expanded", "false");
    trigger.setAttribute(
      "aria-label",
      trigger.dataset.openLabel ?? "Open menu"
    );
    nav.style.top = "";
    document.getElementById("header-group")?.removeAttribute("data-mobile-nav-open");
    this.#maybeHideBackdrop();
    this.#setBodyScrollLock(false);
    if (restoreFocus) {
      trigger.focus({ preventScroll: true });
    }
  }
  #showBackdrop() {
    const backdrop = this.#backdrop;
    if (!backdrop) return;
    backdrop.hidden = false;
    requestAnimationFrame(() => backdrop.setAttribute("data-open", "true"));
  }
  #maybeHideBackdrop() {
    if (this.#isMobileNavOpen) return;
    if (this.#openMegamenus.size > 0) return;
    const backdrop = this.#backdrop;
    if (!backdrop) return;
    backdrop.setAttribute("data-open", "false");
    const cleanup = () => {
      if (backdrop.getAttribute("data-open") === "false") {
        backdrop.hidden = true;
      }
      backdrop.removeEventListener("transitionend", cleanup);
    };
    backdrop.addEventListener("transitionend", cleanup);
  }
  #closeAllMegamenus() {
    if (this.#openMegamenus.size === 0) return;
    for (const id of [...this.#openMegamenus]) {
      const dialog = document.querySelector(`dialog-component#${CSS.escape(id)}`);
      dialog?.closeDialog?.();
    }
  }
  #setBodyScrollLock(locked) {
    if (locked) {
      document.documentElement.setAttribute("scroll-lock", "");
    } else {
      document.documentElement.removeAttribute("scroll-lock");
    }
  }
  #openSearch() {
    const dialog = this.#searchDialog;
    dialog?.showDialog?.();
  }
  #toggleMegamenu(trigger) {
    const id = trigger.getAttribute("aria-controls");
    if (!id) return;
    const dialog = document.querySelector(`dialog-component#${CSS.escape(id)}`);
    dialog?.toggleDialog?.();
  }
  #onDialogOpen = (event) => {
    const id = event.target?.id;
    if (!id) return;
    if (id === "header-search-dialog") {
      this.#searchTrigger?.setAttribute("aria-expanded", "true");
      return;
    }
    const trigger = this.#megamenuTriggerFor(id);
    if (!trigger) return;
    trigger.setAttribute("aria-expanded", "true");
    this.#openMegamenus.add(id);
    const dialogComponent = document.getElementById(id);
    if (dialogComponent) {
      const group = document.getElementById("header-group");
      const bar = document.querySelector(
        ".shopify-section.announcement-bar"
      );
      const groupH = group?.offsetHeight ?? 0;
      const barH = bar?.offsetHeight ?? 0;
      const barInGroup = bar ? group?.contains(bar) : false;
      const headerOnlyH = barInGroup ? groupH - barH : groupH;
      const realScrollY = Math.abs(parseInt(document.body.style.top) || 0);
      const visibleBarH = Math.max(0, barH - realScrollY);
      const topPx = `${headerOnlyH + visibleBarH}px`;
      const innerDialog = dialogComponent.querySelector("dialog");
      if (innerDialog) {
        innerDialog.style.setProperty("top", topPx, "important");
        innerDialog.style.setProperty(
          "max-height",
          `calc(100dvh - ${topPx})`,
          "important"
        );
      }
    }
    this.#showBackdrop();
    this.#syncMegamenuOpenAttr();
  };
  #onDialogClose = (event) => {
    const id = event.target?.id;
    if (!id) return;
    if (id === "header-search-dialog") {
      const trigger2 = this.#searchTrigger;
      if (!trigger2) return;
      trigger2.setAttribute("aria-expanded", "false");
      trigger2.focus();
      return;
    }
    const trigger = this.#megamenuTriggerFor(id);
    if (!trigger) return;
    trigger.setAttribute("aria-expanded", "false");
    trigger.focus();
    this.#openMegamenus.delete(id);
    const dialogComponent = document.getElementById(id);
    if (dialogComponent) {
      const innerDialog = dialogComponent.querySelector("dialog");
      if (innerDialog) {
        innerDialog.style.removeProperty("top");
        innerDialog.style.removeProperty("max-height");
      }
    }
    this.#maybeHideBackdrop();
    this.#syncMegamenuOpenAttr();
  };
  #syncMegamenuOpenAttr() {
    const group = document.getElementById("header-group");
    if (!group) return;
    if (this.#openMegamenus.size > 0) {
      group.setAttribute("data-megamenu-open", "");
    } else {
      group.removeAttribute("data-megamenu-open");
    }
  }
  #megamenuTriggerFor(id) {
    return this.querySelector(
      `[data-megamenu-trigger][aria-controls="${CSS.escape(id)}"]`
    );
  }
}
if (!customElements.get("header-component")) {
  customElements.define("header-component", HeaderComponent);
}
